import {
  get,
  useFormContext,
  type FieldValues,
  type Path,
  type RegisterOptions,
  type UseFormRegisterReturn,
} from "react-hook-form";

/**
 * Lo que TODO campo conectado necesita del formulario que lo rodea.
 *
 * Antes cada pantalla repetía las mismas tres líneas por campo:
 *
 *   disabled={isSubmitting}
 *   error={errors.email?.message}
 *   {...register("email")}
 *
 * Tres cosas mecánicas que no describen el campo, solo lo cablean. Aquí se
 * cablea una vez y ningún formulario del sistema vuelve a escribirlas.
 */
export interface FieldConnection {
  /** Lo que devuelve `register(name)`: `name`, `onChange`, `onBlur` y `ref`. */
  registration: UseFormRegisterReturn;
  /** El mensaje de error de ESTE campo, si el formulario tiene uno. */
  error: string | undefined;
  /** `true` mientras el formulario se está enviando. */
  disabled: boolean;
}

/**
 * Engancha un campo al formulario que lo envuelve (`<Form>` → `FormProvider`).
 *
 * `name` admite rutas anidadas (`"direccion.calle"`): el error se busca con
 * el `get` de react-hook-form, que entiende la ruta igual que `register`.
 * Leer `errors` con `errors[name]` habría funcionado solo en el nivel
 * superior, y el día que exista un formulario con objetos anidados el
 * error simplemente no se pintaría — un fallo silencioso.
 *
 * Leer `formState.errors` y `formState.isSubmitting` aquí es lo que
 * SUSCRIBE al campo a esos cambios: `formState` es un Proxy y react-hook-form
 * solo re-renderiza por lo que alguien leyó.
 */
export function useFieldConnection(
  name: string,
  options?: RegisterOptions<FieldValues>,
): FieldConnection {
  const { register, formState } = useFormContext();
  const message: unknown = get(formState.errors, name)?.message;

  return {
    registration: register(name as Path<FieldValues>, options),
    // Un mensaje de zod es `string`, pero el tipo de react-hook-form admite
    // `ReactNode`. `Input` espera `string`: se filtra en vez de mentirle.
    error: typeof message === "string" ? message : undefined,
    disabled: formState.isSubmitting,
  };
}

/** Props comunes a todo campo conectado. */
export interface ConnectedFieldProps {
  /** Nombre en el formulario. Admite rutas anidadas: `"direccion.calle"`. */
  name: string;
  /** Opciones de `register` — p. ej. `setValueAs` o reglas nativas. */
  registerOptions?: RegisterOptions<FieldValues>;
}
